import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { AssessmentButton, Bullets, TypeSelect } from "ui-components";
import "./SkillCard.scss";
import { useTranslation } from "react-i18next";
import { handlePopupOpen } from "../../pages/Assessment/sections/AssessmentBuild/SidebarCategory";
import { modifyKeywordsDataOnEdit } from "../../utils/Common/Constant";
import { useNavigate } from "react-router-dom";
import AiStarIcon from "../../utils/Images/AiStarIcon";
interface SkillCardProps {
  addedSkills?: any;
  skillItem: any;
  handleAddSkill: (skill: any) => void;
  isAdded?: boolean;
  textBtn: string;
  setIsAddedSKillPopupOpen?: any;
  hideButton?: boolean;
  skillKeywords?: any;
  setSelectedLevel?: any;
  setAddedKeywordsList?: any;
  showIcon?: boolean;
  selectedLevel?: any;
}
const SkillCard = forwardRef(
  (
    {
      addedSkills,
      skillItem,
      handleAddSkill,
      isAdded,
      textBtn,
      setIsAddedSKillPopupOpen,
      hideButton,
      skillKeywords,
      setSelectedLevel,
      setAddedKeywordsList,
      showIcon = true,
      selectedLevel,
    }: SkillCardProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const queryParams = new URLSearchParams(location.search);
    const navigate = useNavigate();
    const [selectedDifficultyLevels, setSelectedDifficultyLevels] = useState<{
      [key: string]: number;
    }>({});
    useEffect(() => {
      setSelectedDifficultyLevels(selectedLevel);
    }, [selectedLevel]);
    useEffect(() => {
      // Initialize the selectedDifficultyLevels with the values from skillItem if it exists
      if (Object.keys(skillItem?.selectedDifficultyLevels ?? {}).length) {
        setSelectedDifficultyLevels(skillItem?.selectedDifficultyLevels);
      } else if (Object.keys(skillItem?.selectDifficultyLevel ?? {}).length) {
        setSelectedDifficultyLevels(skillItem?.selectDifficultyLevel);
      } else {
        setSelectedDifficultyLevels({});
      }
    }, [skillItem, addedSkills]);

    useImperativeHandle(ref, () => ({
      addSkill() {
        handleAddSkill({
          ...skillItem,
          selectedDifficultyLevels,
          skillKeywords,
        });
      },
    }));

    const handleAddClick = useCallback(() => {
      handleAddSkill({
        ...skillItem,
        selectedDifficultyLevels,
        skillKeywords,
      });
    }, [handleAddSkill, skillItem, selectedDifficultyLevels, skillKeywords]);

    const handleUpdateSkill = useCallback(() => {
      const addedSkillData = addedSkills?.filter(
        (item: any) => item.skillId === skillItem?.skillId,
      );
      setAddedKeywordsList(
        modifyKeywordsDataOnEdit(addedSkillData[0]?.skillKeywords) ?? [],
      );
      handlePopupOpen(
        skillItem?.skillId,
        addedSkillData[0],
        setIsAddedSKillPopupOpen,
        setSelectedLevel,
        navigate,
        queryParams,
      );
    }, [
      addedSkills,
      skillItem,
      setAddedKeywordsList,
      setIsAddedSKillPopupOpen,
      setSelectedLevel,
    ]);

    const handleTypeSelectChange = useCallback(
      (level: string, value: number) => {
        setSelectedDifficultyLevels((prev) => ({
          ...prev,
          [level]: value,
        }));
        setSelectedLevel?.((prev: any) => {
          const updated = { ...prev };
          if (value === 0) {
            delete updated[level]; // Remove the level if value is 0
          } else {
            updated[level] = value; // Add/update the level if value is non-zero
          }
          return updated;
        });
      },
      [setSelectedLevel],
    );

    return (
      <div
        className={`skillCard mb-3 ${isAdded ? "cardAdded" : ""}`}
        onClick={isAdded ? handleUpdateSkill : () => {}}
      >
        {showIcon ? (
          <>
            <div className="skillHeading">
              <div className="skillName">
                <div className="skillFirstLetter">
                  {skillItem?.icon || skillItem?.skillIcon ? (
                    <img src={skillItem?.icon ?? skillItem?.skillIcon} alt="" />
                  ) : (
                    <span className="skillChar">
                      {skillItem?.skillName?.charAt(0)}
                    </span>
                  )}
                </div>
                <div className="skillEllip">
                  <span title={skillItem?.skillName}>
                    {skillItem?.skillName}
                  </span>
                </div>
              </div>
              <div className="skillName skillCardsGridType justify-content-start">
                <div className="d-flex align-items-center">
                  <span>{skillItem?.questionType}</span>
                  <div>
                    {skillItem?.questionType === "Video" ? (
                      <AiStarIcon />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            <div className="levels mt-4 mb-3">
              <span>{t("CREATE_ASSESSMENT.DIFFICULTY_LEVELS")}</span>
              <span>{t("CREATE_ASSESSMENT.NO_OF_QUESTIONS")}</span>
            </div>

            <div className="difficultyLevel">
              <div className="levelSec">
                {(skillItem?.difficultyLevels ?? skillItem?.difficultyLevel)
                  ?.split(",")
                  ?.map((level: string) => level.trim())
                  ?.sort((a: string | number, b: string | number) => {
                    const order: Record<string, number> = {
                      Beginner: 0,
                      Intermediate: 1,
                      Expert: 2,
                    };
                    return (order[a] ?? 99) - (order[b] ?? 99);
                  })
                  ?.map((element: string, ind: number) => (
                    <div
                      className="skillCardSelect d-flex justify-content-between mb-3"
                      key={ind}
                    >
                      <span>{element}</span>
                      <TypeSelect
                        className={`${isAdded ? "customSelectTypeDisabled" : ""}`}
                        options={Array.from(
                          {
                            length:
                              skillItem?.totalQuestions + 1 ||
                              skillItem?.questionsCount + 1,
                          },
                          (v, k) => ({
                            label: String(k).padStart(2),
                            value: k,
                          }),
                        )}
                        loadingText="0"
                        value={
                          selectedDifficultyLevels[element] ??
                          skillItem?.questionCount
                        }
                        onChange={(value: any) =>
                          handleTypeSelectChange(element, value)
                        }
                        placeholder="0"
                      />
                    </div>
                  ))}
              </div>
              {!hideButton && (
                <div className="addBtn">
                  <AssessmentButton
                    className={isAdded ? "" : "lightBtn"}
                    onClick={!isAdded ? handleAddClick : () => {}}
                  >
                    {isAdded ? t("GENERAL.UPDATE") : textBtn}
                  </AssessmentButton>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="skillHeading">
              <div className="questionType">
                <div className="questionTypeText">
                  {t("CREATE_ASSESSMENT.QUESTION_TYPE")}
                </div>
                <div className="d-flex align-items-center">
                  <span className="questionTypeValue">
                    {skillItem?.questionType}
                  </span>
                  <div>
                    {skillItem?.questionType === "Video" ? (
                      <AiStarIcon />
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="skillName skillCardsGridType justify-content-start">
                <div className="skillFirstLetter">
                  {skillItem?.icon || skillItem?.skillIcon ? (
                    <img src={skillItem?.icon ?? skillItem?.skillIcon} alt="" />
                  ) : (
                    <span className="skillChar">
                      {skillItem?.skillName?.charAt(0)}
                    </span>
                  )}
                </div>
                <div className="skillEllip">
                  <span title={skillItem?.skillName}>
                    {skillItem?.skillName}
                  </span>
                </div>
              </div>
            </div>
            <div className="availableLevels mt-3 mb-2">
              <span>{t("CREATE_ASSESSMENT.AVAILABLE_DIFFICULTY_LEVELS")}</span>
            </div>

            <div className="difficultyLevel">
              <div className="levelSec d-flex gap-2">
                {(skillItem?.difficultyLevels ?? skillItem?.difficultyLevel)
                  ?.split(",")
                  ?.map((level: string) => level.trim())
                  ?.sort((a: string | number, b: string | number) => {
                    const order: Record<string, number> = {
                      Beginner: 0,
                      Intermediate: 1,
                      Expert: 2,
                    };
                    return (order[a] ?? 99) - (order[b] ?? 99);
                  })
                  ?.map((element: string, ind: number) => (
                    <div
                      key={ind}
                      className="skillCardSelect d-flex justify-content-between mb-2"
                    >
                      <Bullets skillName={element} />
                    </div>
                  ))}
              </div>
              {!hideButton && (
                <div className="addBtn mt-3">
                  <AssessmentButton
                    className={"lightBtn"}
                    onClick={!isAdded ? handleAddClick : () => {}}
                  >
                    {isAdded ? t("GENERAL.UPDATE") : textBtn}
                  </AssessmentButton>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  },
);

SkillCard.displayName = "SkillCard";
export default React.memo(SkillCard);
